<template>
    <div class="app-issue-tracker-editor">
        <template>
            <div class="page-main__section">
                <div class="page-main__section-header">
                    <h2 class="page-main__section-title">Issue Tracker
                        <span v-if="isIntegrated">
                            <span class="element-badge is--success">Linked</span>
                        </span>
                        <span v-else class="element-badge is--alt">Unlinked</span>
                    </h2>
                    <div class="page-main__buttons">
                        <a v-if="hostissuetrackerUrl && isIntegrated" 
                            :href="hostissuetrackerUrl"
                            class="element-button--secondary element-button"
                            target="_blank">
                                <svg class="element-icon--open-new-window element-icon" width="1em" height="1em" aria-hidden="true"><use href="/img/icons.svg#icon-open-new-window"></use></svg>
                                View Tracker
                        </a>
                        <span
                            class="app-tooltip__wrapper--manage-tracker app-tooltip__wrapper"
                            @mouseenter="!isAdmin && (showDisabledTooltip = true)"
                            @mouseleave="showDisabledTooltip = false"
                        >
                            <button :class="['element-button--secondary', 'element-button', { 'is-disabled': !isAdmin}]" :disabled="!isAdmin" @click="toggleManagement()">Manage Tracker</button>
                            <div :class="['app-tooltip--manage-tracker', 'app-tooltip', { 'is--open': showDisabledTooltip }]">
                                <h4 class="app-tooltip__title">You're not added to this project</h4>
                                <div class="app-tooltip__body">This feature is only available to those who are added to the project as Owner.</div>
                            </div>
                        </span>
                    </div>
                </div>
                <div class="page-main__section-body">
                    <p class="element-instructions">Create, or manage, a connection with Issue Tracker. This will allow you to connect review links directly to this tracker for each of your courses. You will be able to select Builder members to add to the Issue Tracker project.</p>
                </div>
            </div>
            <div v-if="message" :class="['message', {'success': message.status === 'success', 'error': message.status === 'error'}]" @click="dismissMessage">{{ message.message }}</div>
        </template>
        <template v-if="showList && !isIntegrated">
        <div :class="['app-modal-dialog--generate-tracker', 'app-modal-dialog', 'modal', { 'hidden': !showList }]" tabindex="-1" role="dialog" aria-modal="true">
            <div class="app-modal-dialog__content">
                <div class="app-modal-dialog__header">
                    <h3 class="app-modal-dialog__title">Manage Issue Tracker Connection</h3>
                </div>
                <div class="app-modal-dialog__body">
                    <p>You are generating a connection with Issue Tracker. This will create an Issue Tracker project that has the same name as this project. It will also be part of the same client—if one exists—or a new client.</p>
                    <div v-show="isLinking" class="app-loader__wrapper">
                        <app-loader />
                    </div>
                </div>
                <div class="app-modal-dialog__footer">
                    <button type="button" class="element-button--secondary element-button" @click="toggleManagement()">Cancel</button>
                    <button type="button" class="element-button--primary element-button" @click="link()" :disabled="isLinking">Generate Tracker</button>
                </div>
            </div>
        </div>
        </template>
        <template v-else-if="showList && isLinked">
            <div :class="['app-modal-dialog--manage-tracker', 'app-modal-dialog', 'modal', { 'hidden': !showList }]" tabindex="-1" role="dialog" aria-modal="true">
                <div class="app-modal-dialog__content">
                    <div class="app-modal-dialog__header">
                        <h3 class="app-modal-dialog__title">Manage Issue Tracker Connection</h3>
                    </div>

                    <div class="app-modal-dialog__body grid">
                        <p class="col-12">Select the Builder members you want added to this Issue Tracker project. Please note that members without current Issue Tracker accounts will have an account created for them and will receive a welcome email.</p>
                        <p class="col-12">Note that if you remove the tracker connection, only the link itself is removed. The Issue Tracker project itself would still exist and must be deleted separately.</p>
                        <table v-if="members.length" class="col-12">
                            <thead>
                                <tr>
                                    <th>Member</th>
                                    <th>Added to Issue Tracker</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="member in members" :key="`member-${member.id}`">
                                    <td>{{ member.name }}
                                        <strong v-if="member.id === user.id" class="app-issue-tracker-editor__you">(You)</strong>
                                    </td>
                                    <td>
                                        <label class="element-toggle">
                                            <input
                                                type="checkbox"
                                                class="element-toggle__toggle"
                                                :checked="existsInIssuetracker(member)"
                                                @change="toggleMember(member, $event)"
                                            />
                                        </label>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="app-modal-dialog__footer">
                        <div class="app-modal-dialog__footer-unlink" v-if="isAdmin && isLinked">
                            <button type="button" class="element-button--text is--alert element-button" @click="unlink()">Delete Issue Tracker Connection</button>
                        </div>

                        <button class="element-button--secondary element-button" @click="toggleManagement()">Cancel</button>
                        <button class="element-button--primary element-button" @click="link(true)">Confirm</button>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import api from '../api/builder.js'

export default {
    components: {

    },
    props: {
        issuetrackerUrl: {
            type: String
        },
        url: {
            type: String
        },
        sourceProject: {
            type: Object
        },
        isAdmin: {
            type: Boolean,
            default: false
        },
        membersList: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object
        },
    },
    data () {
        return {
            showList: false,
            members: this.membersList,
            payload: [],
            project: this.sourceProject,
            message: null,
            isLinking: false,
            issueTrackerData: {},
            generateAnIssueTrackerConnection: false,
            showDisabledTooltip: false,
        }
    },
    computed: {
        issueTrackerMembers () {
            return _.map(this.issueTrackerData?.project?.users, 'email')
        },
        isIntegrated () {
            return this.issuetrackerUrl && this.project?.settings?.issuetracker ? true : false
        },
        isLinked () {
            return  this.issueTrackerData?.project?.issuetracker_uuid ? true : false
        },
        hostissuetrackerUrl () {
            if (this.isIntegrated && this.project?.settings?.issuetracker)
                return this.issuetrackerUrl + "/requests/bug_list/" + this.project?.settings?.issuetracker + "?dashboard=1" // dashboard=1 redirects to standard project requests page
            return null
        }
    },
    methods: {
        async getMembers () {
            try {
                const response = await api.getIssuetracker(this.project.id)
                this.issueTrackerData = _.get(response, 'data')
            } catch (err) {
                console.error(err)
            }
            try {
                const response = await axios.get(this.url)
                this.members = _.get(response, 'data.data.results')
            } catch (err) {
                console.error(err)
            }
            console.log('Issuetracker integrated?', this.isIntegrated)
            console.log('Members', this.members)
            console.log('Tracker data', this.issueTrackerData)
            console.log('Tracker members', this.issueTrackerMembers)
        },
        toggleManagement () {
            if (!this.isAdmin) return
            this.showList = !this.showList

            if (this.showList)
                this.getMembers()
        },
        dismissMessage () {
            this.message = null
        },
        existsInIssuetracker (user) {
            const userEmail = user.email.toLowerCase();
            return (this.payload.find(item => item.email.toLowerCase() === userEmail && item.action === 'add') || this.issueTrackerMembers.map(e => e.toLowerCase()).includes(userEmail)) && !(this.payload.find(item => item.email.toLowerCase() === userEmail && item.action === 'remove'))
        },
        toggleMember (member, event) {
            const isChecked = event.target.checked
            if (isChecked)
                this.addUser(member)
            else
                this.removeUser(member)
        },
        addUser (user) {
            const userData = {
                email: user.email,
                action: 'add'
            }
            const userEmail = user.email.toLowerCase();
            if (this.payload.find(item => item.email.toLowerCase() === userEmail && item.action === 'remove')) {
                this.payload = this.payload.filter(item => !(item.email.toLowerCase() === userEmail && item.action === 'remove'))
            }
            console.log('Adding user to payload', this.payload)
            this.payload.push(userData)
        },
        removeUser (user) {
            const userData = {
                email: user.email,
                action: 'remove'
            }
            console.log('Removing user to payload', this.payload, this.issueTrackerData)
            const userEmail = user.email.toLowerCase();
            if (this.payload.find(item => item.email.toLowerCase() === userEmail && item.action === 'add')) {
                this.payload = this.payload.filter(item => !(item.email.toLowerCase() === userEmail && item.action === 'add'))
            }
            console.log('Adding user to payload', this.payload)
            this.payload.push(userData)
        },
        async link (closeOnSuccess = false) {
            this.isLinking = true
            this.message = null
            let payload = { users: this.payload }

            try {
                const response = await api.linkIssuetracker(this.project.id, payload)
                if(response)
                    {
                        this.issueTrackerData = _.get(response?.issuetracker, 'data') || {}
                        this.message = { status: 'success', message: 'Success!' }
                        if (response?.project)
                            this.project = response.project
                    } else {
                        throw new Error('No response received from Issue Tracker')
                    }
            } catch (err) {
                console.error(err)
                this.message = { status: 'error', message: 'An error occurred' }
            }
            this.isLinking = false
            if (closeOnSuccess && this.message?.status === 'success') {
                this.showList = false
                this.payload = []
            }
        },
        async unlink () {
            if (!confirm('Warning, are you sure you want to unlink? Relinking the project will generate a new Issue Tracker and all existing issues will not be included.')) {
                return
            }

            try {
                const response = await api.unlinkIssuetracker(this.project.id)
                this.issueTrackerData = {}
                if (response?.project)
                    this.project = response.project
                this.message = { status: 'success', message: 'Issue Tracker unlinked successfully.' }
                this.payload = []
                this.issueTrackerData = {}
            } catch (err) {
                console.error(err)
                this.message = { status: 'error', message: 'An error occurred while unlinking the Issue Tracker.' }
            }
        },
    }
}
</script>